import { Component, EventEmitter, Input, Output, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { faPlay, faTimes, faToggleOn, faToggleOff, faCheckSquare, faSquare, faGear } from '@fortawesome/free-solid-svg-icons';
import { ButtonComponent } from '../button/button.component';
import { ModalOcrOverrideComponent } from '../modal-ocr-override/modal-ocr-override.component';
import { Configuration } from '../../services/configurations.service';
import { ExecuteOptions as BaseExecuteOptions } from '../../services/interactions.service';
import { FileGroup } from '../../services/file-groups.service';
import { Archive } from '../../services/archives.service';
import { BenchmarkService } from '../../services/benchmark.service';
import { TruncateMiddlePipe } from '../../pipes/truncate-middle.pipe';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';

export type BatchAxis = 'delfos_llm_config_id' | 'di2winocr_features_config_id';

export type RunScope = 'all' | 'files' | 'groups';

export interface ExecuteOptions extends BaseExecuteOptions {
  save_as_default?: boolean;
  batch_axis?: BatchAxis | null;
  batch_ids?: string[];
}

@Component({
  selector: 'app-modal-execute-interaction',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, ButtonComponent, NgSelectModule, ModalOcrOverrideComponent, TruncateMiddlePipe],
  templateUrl: './modal-execute-interaction.component.html',
  styleUrls: ['./modal-execute-interaction.component.scss']
})
export class ModalExecuteInteractionComponent implements OnInit{

  constructor(
    private benchmarkService: BenchmarkService,
    private translate: TranslateService) {}

  faPlay = faPlay;
  faTimes = faTimes;
  faToggleOn = faToggleOn;
  faToggleOff = faToggleOff;
  faCheckSquare = faCheckSquare;
  faSquare = faSquare;
  faGear = faGear;
  infoIcon = faCircleInfo;
  showBenchmarkModal = false;
  showOcrOverride = false;
  ocrOverride: Record<string, any> | null = null;
  isAdmin = false;
  benchmarkBadges: Record<string, string[]> = {};
  benchmarkCategories: any[] = [];
  benchmarkSummary: string = '';
  badgeIcons: Record<string, any> = {};

  @Input() delfosServerConfigs: Configuration[] = [];
  @Input() delfosLlmConfigs: Configuration[] = [];
  @Input() di2winocrServerConfigs: Configuration[] = [];
  @Input() di2winocrFeaturesConfigs: Configuration[] = [];
  @Input() hasReferences: boolean = false;
  @Input() fileGroups: FileGroup[] = [];
  @Input() archives: Archive[] = [];

  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<ExecuteOptions>();

  @Input() set initialOptions(opts: ExecuteOptions | null) {
    this._initialOptions = opts;
  }
  private _initialOptions: ExecuteOptions | null = null;

  @Input() set show(val: boolean) {
    const wasClosed = !this._show && val;
    this._show = val;
    if (wasClosed) {
      this.resetOptions();
      if (this._initialOptions) {
        this.options = { ...this.options, ...this._initialOptions };
      }
    }
  }
  get show(): boolean {
    return this._show;
  }
  private _show = false;
  options: ExecuteOptions = {
    delfos_server_config_id: null,
    delfos_llm_config_id: null,
    di2winocr_server_config_id: null,
    di2winocr_features_config_id: null,
    clear_delfos_cache: false,
    clear_di2winocr_cache: false,
    use_multimodal: false,
    ai_model_language: 'pt',
    save_as_default: false,
    references_mode: 'output_only'
  };

  batchMode: boolean = false;
  batchAxis: BatchAxis = 'delfos_llm_config_id';
  batchLlmIds: string[] = [];
  batchOcrFeatureIds: string[] = [];

  runScope: RunScope = 'all';
  selectedGroupIds: string[] = [];
  selectedFileIds: string[] = [];

  aiModelLanguageOptions = [
    { value: 'pt', label: 'SETTINGS.PREFERENCES.AI_MODEL_LANGUAGE_OPTIONS.PT' },
    { value: 'en', label: 'SETTINGS.PREFERENCES.AI_MODEL_LANGUAGE_OPTIONS.EN' },
    { value: 'es', label: 'SETTINGS.PREFERENCES.AI_MODEL_LANGUAGE_OPTIONS.ES' }
  ];

  referencesModeOptions = [
    { value: 'output_only', label: 'SETTINGS.PREFERENCES.REFERENCES_MODE_OPTIONS.OUTPUT_ONLY' },
    { value: 'input_output', label: 'SETTINGS.PREFERENCES.REFERENCES_MODE_OPTIONS.INPUT_OUTPUT' }
  ];

  private resetOptions() {
    this.options = {
      delfos_server_config_id: null,
      delfos_llm_config_id: null,
      di2winocr_server_config_id: null,
      di2winocr_features_config_id: null,
      clear_delfos_cache: false,
      clear_di2winocr_cache: false,
      use_multimodal: false,
      ai_model_language: 'pt',
      save_as_default: false,
      references_mode: 'output_only'
    };
    this.batchMode = false;
    this.batchAxis = 'delfos_llm_config_id';
    this.batchLlmIds = [];
    this.batchOcrFeatureIds = [];
    this.ocrOverride = null;
    this.showOcrOverride = false;
    this.runScope = 'all';
    this.selectedGroupIds = [];
    this.selectedFileIds = [];
  }

  toggleOption(key: keyof ExecuteOptions) {
    if (typeof this.options[key] === 'boolean') {
      (this.options as any)[key] = !this.options[key];
      if (key === 'use_multimodal' && this.options.use_multimodal) {
        this.ocrOverride = null;
        if (this.batchAxis === 'di2winocr_features_config_id') {
          this.batchMode = false;
        }
      }
    }
  }

  get selectedOcrConfig(): Configuration | null {
    const id = this.options.di2winocr_features_config_id;
    if (!id) {
      return null;
    }
    return this.di2winocrFeaturesConfigs.find(c => c.id === id) || null;
  }

  get canEditOcr(): boolean {
    return !this.options.use_multimodal
      && !(this.batchMode && this.batchAxis === 'di2winocr_features_config_id')
      && !!this.selectedOcrConfig;
  }

  openOcrOverride() {
    if (!this.canEditOcr) {
      return;
    }
    this.showOcrOverride = true;
  }

  onOverrideApply(override: Record<string, any>) {
    this.ocrOverride = override;
    this.showOcrOverride = false;
  }

  onOverrideRestore() {
    this.ocrOverride = null;
    this.showOcrOverride = false;
  }

  onOverrideClose() {
    this.showOcrOverride = false;
  }

  onOcrSelectionChange() {
    this.ocrOverride = null;
  }

  onConfirm() {
    if (this.batchMode) {
      const ids = this.batchIds;
      if (!ids.length) {
        return;
      }
      this.confirm.emit({ ...this.options, archive_ids: this.selectedArchiveIds, batch_axis: this.batchAxis, batch_ids: ids });
      return;
    }
    const payload: ExecuteOptions = { ...this.options, archive_ids: this.selectedArchiveIds };
    payload.di2winocr_features_override = (this.ocrOverride && this.canEditOcr) ? this.ocrOverride : null;
    this.confirm.emit(payload);
  }

  onClose() {
    this.close.emit();
  }

  get showDelfosServer(): boolean {
    return this.delfosServerConfigs.length > 1;
  }

  get showDelfosLlm(): boolean {
    return this.delfosLlmConfigs.length > 1;
  }

  get showOcrServer(): boolean {
    return this.di2winocrServerConfigs.length > 1;
  }

  get showOcrFeatures(): boolean {
    return this.di2winocrFeaturesConfigs.length > 1;
  }

  get showConfigsSection(): boolean {
    return this.showDelfosServer || this.showDelfosLlm || this.showOcrServer || this.showOcrFeatures;
  }

  get canBatchByLlm(): boolean {
    return this.delfosLlmConfigs.length > 1;
  }

  get canBatchByOcr(): boolean {
    return this.di2winocrFeaturesConfigs.length > 1 && !this.options.use_multimodal;
  }

  get canBatch(): boolean {
    return this.canBatchByLlm && this.canBatchByOcr;
  }

  get batchIds(): string[] {
    return this.batchAxis === 'delfos_llm_config_id' ? this.batchLlmIds : this.batchOcrFeatureIds;
  }

  get isExecuteDisabled(): boolean {
    if (this.runScope !== 'all') {
      const ids = this.selectedArchiveIds;
      if (!ids || ids.length === 0) {
        return true;
      }
    }
    return this.batchMode && this.batchIds.length === 0;
  }

  get selectedArchiveIds(): string[] | null {
    if (this.runScope === 'files') {
      if (!this.selectedFileIds.length) {
        return null;
      }
      return this.selectedFileIds.map(id => String(id));
    }
    if (this.runScope !== 'groups' || !this.selectedGroupIds.length) {
      return null;
    }
    const selectedGroups = this.fileGroups.filter(g => this.selectedGroupIds.includes(g.id));
    const union = new Set<string>();
    selectedGroups.forEach(group => (group.archive_ids || []).forEach(id => union.add(String(id))));
    return Array.from(union);
  }

  get showFilesScope(): boolean {
    return this.archives.length > 0 || this.fileGroups.length > 0;
  }

  setRunScope(scope: RunScope) {
    if (this.runScope === scope) {
      return;
    }
    this.runScope = scope;
    if (scope !== 'groups') {
      this.selectedGroupIds = [];
    }
    if (scope !== 'files') {
      this.selectedFileIds = [];
    }
  }

  get executeButtonLabel(): string {
    if (this.batchMode && this.batchIds.length > 0) {
      return this.translate.instant('EXECUTE_MODAL.EXECUTE_BATCH', { count: this.batchIds.length });
    }
    return this.translate.instant('EXECUTE_MODAL.EXECUTE');
  }

  toggleBatchMode() {
    this.batchMode = !this.batchMode;
    if (this.batchMode) {
      this.batchAxis = this.canBatchByLlm ? 'delfos_llm_config_id' : 'di2winocr_features_config_id';
      this.batchLlmIds = [];
      this.batchOcrFeatureIds = [];
      this.options.save_as_default = false;
    }
  }

  setBatchAxis(axis: BatchAxis) {
    if (this.batchAxis === axis) {
      return;
    }
    this.batchAxis = axis;
    this.batchLlmIds = [];
    this.batchOcrFeatureIds = [];
  }

  openBenchmarkModal() {
    if (!this.isAdmin) {
      return;
    }
    this.showBenchmarkModal = true;
  }

  closeBenchmarkModal() {
    this.showBenchmarkModal = false;
  }

  getBadgesForModel(model: string): string[] {
    return this.benchmarkBadges[model] || [];
  }

  resolveIcon(iconName: string) {
    const formatted = "fa" + iconName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
    return (solidIcons as any)[formatted] || solidIcons.faCircleInfo;
  }

  processBenchmark(benchmark: any) {
    const modelMetrics: any = {};
    const badgeDefs = benchmark.badges_definition;
    benchmark.model_results.forEach((m: any) => {
      modelMetrics[m.llm_model] = m;
    });
    this.benchmarkCategories = [];
    Object.entries(badgeDefs).forEach(([key, def]: any) => {
      const ranking = benchmark.rankings[key];
      if (!ranking) return;
      const models = ranking
        .filter((model: string) =>
          this.benchmarkBadges[model]?.includes(key)
        )
        .map((model: string) => {
          const metrics = modelMetrics[model];
          let metricValue = "";
          if (def.metric === "execution_time") {
            const times = Object.values(metrics.execution_time_s)
              .filter((v): v is number => typeof v === 'number');
            const fastest = Math.min(...times);
            metricValue = fastest.toFixed(1) + "s";
          }
          if (def.metric === "f1_score") {
            const f1 = metrics.performance_f1_score[key];
            metricValue = f1 !== undefined
              ? f1.toFixed(3)
              : "-";
          }
          return {
            model,
            metric: metricValue
          };
        });

      this.benchmarkCategories.push({
        key,
        title: 'BENCHMARK.CATEGORIES.' + key.toUpperCase(),
        description: 'BENCHMARK.CATEGORIES_DESC.' + key.toUpperCase(), //
        icon: this.resolveIcon(def.icon),
        models
      });
    });
  }

  buildModelBadges(benchmark: any) {
    const badges: any = {};
    const defs = benchmark.badges_definition;
    benchmark.model_results.forEach((m: any) => {
      badges[m.llm_model] = [];
    });
    Object.entries(defs).forEach(([key, def]: any) => {
      if (def.metric === "execution_time") {
        const ranking = benchmark.rankings.fastest || [];
        ranking.slice(0, 3).forEach((model: string) => {
          badges[model].push(key);
        });
        return;
      }

      if (def.metric === "f1_score") {
        benchmark.model_results.forEach((model: any) => {
          const f1 = model.performance_f1_score[key];
          if (f1 !== undefined && f1 >= 0.9) {
            badges[model.llm_model].push(key);
          }
        });
      }
    });
    this.benchmarkBadges = badges;
  }

  generateBenchmarkSummary(benchmark: any) {
    const setup = benchmark.execution_setup;
    this.benchmarkSummary = this.translate.instant(
      'BENCHMARK.SUMMARY',
      {
        models: setup.models,
        documents: setup.documents_per_category,
        workspaces: setup.categories,
        primary: benchmark.evaluation_criteria.primary_metric,
        secondary: this.translate.instant('BENCHMARK.EXECUTION_TIME'),
        ocr: setup.ocr,
        layout: setup.layout
      }
    );
  }

  ngOnInit() {

    const role = sessionStorage.getItem('role');
    this.isAdmin = role === 'ADMIN';
    if (!this.isAdmin) {
      return;
    }
    this.benchmarkService.getBenchmark().subscribe((data: any) => {
      const benchmark = data.benchmark;
      Object.entries(benchmark.badges_definition).forEach(([key, def]: any) => {
        this.badgeIcons[key] = this.resolveIcon(def.icon);
      });
      this.buildModelBadges(benchmark);
      this.generateBenchmarkSummary(benchmark);
      this.processBenchmark(benchmark);

    });

  }

}
